import React from 'react';
import { Image, Platform, StyleSheet, Text, View } from 'react-native';
import { useHydratedWindowDimensions } from '../../hooks/useHydratedWindowDimensions';
import { Link } from 'expo-router';
import colors from '../../lib/colors';
import { interMedium, interRegular, interSemiBold } from '../../lib/fonts';
import { responsive, responsiveMultiple } from '../../lib/responsive';
import { Body, Heading, Section, SectionLabel } from '../about/AboutPrimitives';
import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';

const SOURCE_LOGOS = [
    { key: 'iacr', label: msg({ id: 'source.iacr', message: 'IACR ePrint' }), source: require('../../assets/source-logos/iacr.png') },
    { key: 'arxiv', label: msg({ id: "home.homesourcesection.arxiv", message: "arXiv", comment: "UI label in components/home/HomeSourceSection.tsx" }), source: require('../../assets/ArXiv_logo_2022.svg') },
    // Current PubMed wordmark from the official NCBI PubMed site.
    { key: 'pubmed', label: msg({ id: "home.homesourcesection.pubmed", message: "PubMed", comment: "UI label in components/home/HomeSourceSection.tsx" }), source: require('../../assets/source-logos/pubmed-logo-blue.svg') },
    { key: 'biorxiv', label: msg({ id: "home.homesourcesection.biorxiv", message: "bioRxiv", comment: "UI label in components/home/HomeSourceSection.tsx" }), source: require('../../assets/biorxiv_logo_homepage.png') },
    { key: 'medrxiv', label: msg({ id: "home.homesourcesection.medrxiv", message: "medRxiv", comment: "UI label in components/home/HomeSourceSection.tsx" }), source: require('../../assets/MedRxiv_homepage_logo.png') },
    { key: 'chemrxiv', label: msg({ id: "home.homesourcesection.chemrxiv", message: "ChemRxiv", comment: "UI label in components/home/HomeSourceSection.tsx" }), source: require('../../assets/chemRxiv.jpeg'), wide: true },
    { key: 'chinaxiv', label: msg({ id: "home.homesourcesection.chinaxiv", message: "ChinaXiv", comment: "UI label in components/home/HomeSourceSection.tsx" }), source: require('../../assets/ChinaXiv_homepage_logo.png') },
];

const SOURCE_GROUPS = [
    {
        title: msg({ id: "home.homesourcesection.paperSources", message: "Paper sources", comment: "UI title in components/home/HomeSourceSection.tsx" }),
        items: [
            msg({ id: 'source.arxiv', message: 'arXiv' }),
            msg({ id: 'source.iacr', message: 'IACR ePrint' }),
            msg({ id: 'source.biorxiv', message: 'bioRxiv' }),
            msg({ id: 'source.medrxiv', message: 'medRxiv' }),
            msg({ id: 'source.chemrxiv', message: 'ChemRxiv' }),
            msg({ id: 'source.chinaxiv', message: 'ChinaXiv' }),
            msg({ id: 'source.pubmed', message: 'PubMed' }),
        ],
        body: msg({ id: "home.homesourcesection.trackNewPreprintsAndNewPubmedIndexedBiomedicalAndClinicalRecordsWithoutManuallyC", message: "Track new preprints and new PubMed-indexed biomedical and clinical records without manually checking each source.", comment: "UI body in components/home/HomeSourceSection.tsx" }),
    },
    {
        title: msg({ id: "home.homesourcesection.forwardedAlerts", message: "Forwarded alerts", comment: "UI title in components/home/HomeSourceSection.tsx" }),
        items: [
            msg({ id: 'source.googleScholarAlerts', message: 'Google Scholar alerts' }),
            msg({ id: 'source.paperAlertEmails', message: 'Paper alert emails' }),
            msg({ id: 'source.journalAlerts', message: 'Journal alerts' }),
            msg({ id: 'source.newsletterAlerts', message: 'Newsletter-style alerts' }),
        ],
        body: msg({ id: "home.homesourcesection.forwardExistingAlertsToPaperzillaAndLetTheRelevanceEngineDecideWhatBelongsInYour", message: "Forward existing alerts to Paperzilla and let the relevance engine decide what belongs in your focused feed.", comment: "UI body in components/home/HomeSourceSection.tsx" }),
    },
    {
        title: msg({ id: "home.homesourcesection.researchDataSources", message: "Research data sources", comment: "UI title in components/home/HomeSourceSection.tsx" }),
        items: [
            msg({ id: 'source.datasets', message: 'Datasets *' }),
            msg({ id: 'source.softwareRepositories', message: 'Software repositories *' }),
            msg({ id: 'source.patentRecords', message: 'Patent records *' }),
            msg({ id: 'source.additionalOnRequest', message: 'Additional sources on request' }),
        ],
        body: msg({ id: "home.homesourcesection.paperRelatedDatasetsSoftwareReposAndPatentsFitIntoTheSameRelevanceFilteredWorkfl", message: "Paper-related datasets, software repos, and patents fit into the same relevance-filtered workflow.", comment: "UI body in components/home/HomeSourceSection.tsx" }),
    },
];

export default function HomeSourceSection({ fontsLoaded }: { fontsLoaded: boolean }) {
    const { i18n } = useLingui();
    const { width } = useHydratedWindowDimensions();
    const isNarrow = width < 760;
    const isTablet = width < 980;
    const groupGridStyle = Platform.OS === 'web' && !isTablet
        ? ({ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 20 } as any)
        : null;

    return (
        <Section backgroundColor={colors.bgWeak} top={80} bottom={80} isNarrow={isNarrow}>
            <SectionLabel fontsLoaded={fontsLoaded}><Trans id="home.homesourcesection.sourcesAndAlerts" comment="UI copy in components/home/HomeSourceSection.tsx">Sources and alerts</Trans></SectionLabel>
            <Heading fontsLoaded={fontsLoaded}><Trans id="home.homesourcesection.bringEveryResearchStreamIntoOneRelevanceFilter" comment="UI copy in components/home/HomeSourceSection.tsx">
                Bring every research stream into one relevance filter
            </Trans></Heading>
            <View style={styles.leadCopy}>
                <Body fontsLoaded={fontsLoaded}><Trans id="home.homesourcesection.paperzillaCanMonitorPreprintSourcesAndPubmedDirectlyAcceptForwarded" comment="UI copy in components/home/HomeSourceSection.tsx">
                    Paperzilla can monitor preprint sources and PubMed directly, accept forwarded alerts, and fold paper-related datasets, software repos, and patents into the same feed. PubMed is included in every paid plan.
                </Trans></Body>
            </View>

            <View style={styles.logoRail} {...responsive('sources-card')}>
                <View style={styles.logoGrid} {...responsive('sources-logos-container')}>
                    {SOURCE_LOGOS.map((logo) => (
                        <View key={logo.key} style={styles.logoSlot} {...responsive('source-logo-wrapper')}>
                            <Image
                                accessibilityLabel={i18n._({
                                    ...msg({ id: 'source.logoAccessibilityLabel', message: '{source} logo' }),
                                    values: { source: i18n._(logo.label) },
                                })}
                                resizeMode="contain"
                                source={logo.source}
                                style={[styles.logoImage, logo.wide && styles.logoImageWide]}
                                {...(
                                    logo.key === 'chemrxiv'
                                        ? responsiveMultiple('source-logo', 'source-logo-chemrxiv')
                                        : logo.key === 'pubmed'
                                            ? responsiveMultiple('source-logo', 'source-logo-pubmed')
                                            : responsive('source-logo')
                                )}
                            />
                        </View>
                    ))}
                </View>
            </View>

            <View style={[styles.groupGrid, isTablet && styles.groupStack, groupGridStyle]}>
                {SOURCE_GROUPS.map((group) => (
                    <View key={String(group.title.id)} style={styles.groupCard}>
                        <Text style={[styles.groupTitle, fontsLoaded && interSemiBold]}>{i18n._(group.title)}</Text>
                        <Text style={[styles.groupBody, fontsLoaded && interRegular]}>{i18n._(group.body)}</Text>
                        <View style={styles.pillWrap}>
                            {group.items.map((item) => (
                                <Text key={String(item.id)} style={[styles.sourcePill, fontsLoaded && interMedium]}>
                                    {i18n._(item)}
                                </Text>
                            ))}
                        </View>
                    </View>
                ))}
            </View>

            <Text style={[styles.moreSourcesNote, fontsLoaded && interRegular]}><Trans id="home.homesourcesection.itemsMarkedWithArePaperRelatedDatasetsSoftwareReposAnd" comment="UI copy in components/home/HomeSourceSection.tsx">
                Items marked with * are paper-related datasets, software repos, and patents. Need another source?{' '}
                <Link href="mailto:hello@paperzilla.ai" style={[styles.moreSourcesLink, fontsLoaded && interSemiBold]}>
                    Let us know
                </Link>
                .
            </Trans>
            </Text>
        </Section>
    );
}

const styles = StyleSheet.create({
    leadCopy: {
        maxWidth: 820,
        marginBottom: 32,
    },
    logoRail: {
        backgroundColor: '#ffffff',
        borderColor: colors.border,
        borderRadius: 18,
        borderWidth: 1.5,
        marginBottom: 24,
        paddingHorizontal: 12,
        paddingVertical: 12,
    },
    logoGrid: {
        alignItems: 'stretch',
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
    },
    logoSlot: {
        alignItems: 'center',
        backgroundColor: '#fbfcfd',
        borderColor: '#edf0f3',
        borderRadius: 11,
        borderWidth: 1,
        flexBasis: 150,
        flexGrow: 1,
        justifyContent: 'center',
        maxWidth: 190,
        minHeight: 86,
        paddingHorizontal: 12,
        paddingVertical: 14,
    },
    logoImage: {
        height: 52,
        maxWidth: 150,
        width: '100%',
    },
    logoImageWide: {
        height: 62,
        maxWidth: 174,
    },
    groupGrid: {
        flexDirection: 'row',
        gap: 20,
    },
    groupStack: {
        flexDirection: 'column',
    },
    groupCard: {
        backgroundColor: '#ffffff',
        borderColor: colors.border,
        borderRadius: 14,
        borderWidth: 1.5,
        flex: 1,
        paddingHorizontal: 22,
        paddingVertical: 24,
    },
    groupTitle: {
        color: colors.text900,
        fontSize: 16,
        fontWeight: '700',
        lineHeight: 22,
        marginBottom: 10,
    },
    groupBody: {
        color: colors.text600,
        fontSize: 14,
        lineHeight: 23,
        marginBottom: 18,
    },
    pillWrap: {
        flexDirection: 'row',
        flexWrap: 'wrap',
    },
    sourcePill: {
        backgroundColor: colors.primary100,
        borderColor: `${colors.paleGreen}70`,
        borderRadius: 999,
        borderWidth: 1,
        color: colors.primaryGreenDark,
        fontSize: 12,
        fontWeight: '600',
        lineHeight: 17,
        marginBottom: 10,
        marginRight: 8,
        paddingHorizontal: 10,
        paddingVertical: 5,
    },
    moreSourcesNote: {
        color: colors.text600,
        fontSize: 14,
        lineHeight: 22,
        marginTop: 18,
        textAlign: 'center',
    },
    moreSourcesLink: {
        color: colors.primaryGreenDark,
        textDecorationLine: 'underline',
    },
});
